import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useHelp } from '../../lib/useHelp';
import HelpCenter from './HelpCenter';

// Panel lateral de ayuda (GOL-11, `AYU-01`). Se abre desde el botón «?» del
// header, el menú de cuenta y el pie del Sidebar, y NO navega: la duda se
// resuelve sin perder la pantalla en la que estabas trabajando.

export default function HelpDrawer() {
  const { open, view, setView, closeHelp } = useHelp();

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') closeHelp();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, closeHelp]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-drawer bg-black/50"
            onClick={closeHelp}
          />
          <motion.aside
            role="dialog"
            aria-label="Ayuda"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 34 }}
            className="fixed inset-y-0 right-0 z-drawer flex w-full max-w-md flex-col border-l border-ui-border bg-ui-bg shadow-xl"
          >
            <header className="flex shrink-0 items-center justify-between border-b border-ui-border px-4 py-3">
              <h2 className="text-sm font-semibold text-ui-text">Ayuda</h2>
              <button
                type="button"
                onClick={closeHelp}
                aria-label="Cerrar ayuda"
                className="flex h-8 w-8 items-center justify-center rounded-md text-ui-text-subtle transition-colors hover:bg-ui-text/[0.06] hover:text-ui-text"
              >
                <X size={16} />
              </button>
            </header>

            {/* El asistente scrollea su propia conversación; el resto de vistas
                scrollean como una hoja larga. Dejar ambos con overflow daría dos
                barras de scroll anidadas sobre el mismo contenido. */}
            <div className={`flex min-h-0 flex-1 flex-col px-4 py-4 ${
              view.kind === 'assistant' ? 'overflow-hidden' : 'overflow-y-auto'
            }`}>
              <HelpCenter
                variant="drawer"
                view={view}
                onViewChange={setView}
                onNavigate={closeHelp}
              />
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
